import { useMemo } from 'react'
import moment from 'moment'
import type { UserData } from '../../types'
import { buildOverview, buildNutrition, periodWindowLabel, PERIOD_DAYS, type MaxRecord, type Period } from '../../utils/analytics'
import { diffHuman } from '../../utils/calc'
import { BarChart } from './ChartCanvas'

const r = (n: number) => Math.round(n)

function NutritionSnapshot({ data, period }: { data: UserData; period: Period }) {
  const n = useMemo(() => buildNutrition(data, period), [data, period])
  const windowDays = PERIOD_DAYS[period]

  if (n.daysLogged === 0) {
    return (
      <section className="card">
        <h3 className="font-semibold mb-1">Nutrition snapshot</h3>
        <p className="text-sm text-[var(--text-secondary)]">Log some food &amp; drink to see your nutrition averages here.</p>
      </section>
    )
  }

  const avg = n.avgByNutrient
  const kcal = r(n.totalCaloriesIn / n.daysLogged)
  const p = r(avg.protein ?? 0)
  const c = r(avg.carbs ?? 0)
  const f = r(avg.fat ?? 0)
  const kP = p * 4
  const kC = c * 4
  const kF = f * 9
  const macroSum = kP + kC + kF || 1
  const pctP = Math.round((kP / macroSum) * 100)
  const pctC = Math.round((kC / macroSum) * 100)
  const pctF = 100 - pctP - pctC
  const proteinPerKg = data.weight ? (avg.protein ?? 0) / data.weight : null
  const balance = n.tdee != null ? kcal - n.tdee : null

  const Stat = ({ label, value, unit }: { label: string; value: number; unit?: string }) => (
    <div className="text-center">
      <div className="text-lg font-bold text-[var(--text-primary)]">
        {value}
        {unit ? <span className="text-xs font-normal text-[var(--text-secondary)]">{unit}</span> : null}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)]">{label}</div>
    </div>
  )

  const watch = [
    { label: 'Fibre', v: r(avg.fibre ?? 0), u: 'g' },
    { label: 'Added sugar', v: r(avg.addedSugar ?? 0), u: 'g' },
    { label: 'Sat fat', v: r(avg.saturatedFat ?? 0), u: 'g' },
    { label: 'Sodium', v: r(avg.sodium ?? 0), u: 'mg' },
  ]

  return (
    <section className="card">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h3 className="font-semibold">Nutrition snapshot</h3>
        <span className="text-xs text-[var(--text-secondary)]">avg per logged day · {n.daysLogged}/{windowDays} days</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Calories" value={kcal} />
        <Stat label="Protein" value={p} unit="g" />
        <Stat label="Carbs" value={c} unit="g" />
        <Stat label="Fat" value={f} unit="g" />
      </div>

      {/* Macro energy split */}
      <div className="mt-4">
        <div className="flex h-2.5 overflow-hidden rounded-full bg-[var(--bg-card)]">
          <div style={{ width: `${pctP}%`, backgroundColor: 'var(--accent-teal)' }} />
          <div style={{ width: `${pctC}%`, backgroundColor: 'var(--accent-orange)' }} />
          <div style={{ width: `${pctF}%`, backgroundColor: 'var(--activity-intake)' }} />
        </div>
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--text-secondary)]">
          <span><span className="mr-1 inline-block h-2 w-2 rounded-full align-middle" style={{ backgroundColor: 'var(--accent-teal)' }} />Protein {pctP}%</span>
          <span><span className="mr-1 inline-block h-2 w-2 rounded-full align-middle" style={{ backgroundColor: 'var(--accent-orange)' }} />Carbs {pctC}%</span>
          <span><span className="mr-1 inline-block h-2 w-2 rounded-full align-middle" style={{ backgroundColor: 'var(--activity-intake)' }} />Fat {pctF}%</span>
          {proteinPerKg != null && <span>· {proteinPerKg.toFixed(1)} g protein/kg</span>}
        </div>
      </div>

      {/* Watch list */}
      <div className="mt-4 grid grid-cols-4 gap-2 border-t border-[var(--border)] pt-3">
        {watch.map((w) => (
          <Stat key={w.label} label={w.label} value={w.v} unit={w.u} />
        ))}
      </div>

      {balance != null && (
        <p className="mt-3 text-sm text-[var(--text-secondary)]">
          Energy balance: averaging{' '}
          <strong className={balance > 0 ? 'text-[var(--accent-orange)]' : 'text-[var(--bac-safe)]'}>
            {balance > 0 ? '+' : ''}{balance} kcal/day
          </strong>{' '}
          vs your TDEE of {n.tdee}.
        </p>
      )}
    </section>
  )
}

const fmtMax = (m: MaxRecord): string =>
  m.date ? diffHuman(moment(m.date, 'YYYY-MM-DD').format('YYYY-MM-DD HH:mm:ss')) : '—'

/** Active-days message, scaled to the window length (thresholds tuned at 30 days). */
function frequencyMsg(activeDays: number, windowDays: number, noun: string): string {
  if (activeDays === 0) return `Log some ${noun} to see insights here.`
  const ratio = activeDays / windowDays
  if (ratio < 0.15) return `You really should move at least once a week.`
  if (ratio < 0.4) return `Your ${noun} logging is good — maybe an extra day a week?`
  if (ratio < 0.53) return `Your ${noun} logging is great! Awesome work!`
  return `Your ${noun} logging is INSANE! Fantastic!`
}

interface SectionProps {
  title: string
  labels: string[]
  data: number[]
  yLabel: string
  summary: React.ReactNode
  secondary?: { data: number[]; yLabel: string }
}

function Section({ title, labels, data, yLabel, summary, secondary }: SectionProps) {
  return (
    <section className="card">
      <h3 className="font-semibold mb-3">{title}</h3>
      <BarChart labels={labels} data={data} yLabel={yLabel} />
      {secondary && (
        <div className="mt-4">
          <BarChart labels={labels} data={secondary.data} yLabel={secondary.yLabel} />
        </div>
      )}
      <div className="mt-3 text-sm text-[var(--text-secondary)] space-y-1">{summary}</div>
    </section>
  )
}

export default function OverviewCharts({ data, period }: { data: UserData; period: Period }) {
  const a = useMemo(() => buildOverview(data, period), [data, period])
  const windowDays = PERIOD_DAYS[period]
  const windowLabel = periodWindowLabel(period)

  const avgTime = a.attendanceDays ? Math.round(a.attendanceTotalMinutes / a.attendanceDays) : 0
  const avgCardio = a.cardioDays ? Math.round(a.cardioCalories_total / a.cardioDays) : 0
  const avgWeights = a.weightsDays ? Math.round(a.weightsKg_total / a.weightsDays) : 0
  const avgWeightsCal = a.weightsDays ? Math.round(a.weightsCalories_total / a.weightsDays) : 0
  const avgExercise = a.exerciseDays ? Math.round(a.exerciseCalories_total / a.exerciseDays) : 0

  return (
    <div className="space-y-4 mt-4">
      <NutritionSnapshot data={data} period={period} />
      <Section
        title="Attendance"
        labels={a.labels}
        data={a.attendanceMinutes}
        yLabel="Minutes"
        summary={
          <>
            <p>You logged moves on {a.attendanceDays} of {windowLabel}. {frequencyMsg(a.attendanceDays, windowDays, 'move')}</p>
            <p>Average {avgTime} minutes per active day.</p>
          </>
        }
      />
      <Section
        title="Cardio"
        labels={a.labels}
        data={a.cardioCalories}
        yLabel="Calories"
        summary={
          <>
            <p>Averaged {avgCardio} calories across {a.cardioDays} cardio days. {frequencyMsg(a.cardioDays, windowDays, 'cardio')}</p>
            <p>Record: <strong className="text-[var(--bac-safe)]">{Math.round(a.cardioMax.value)}</strong> cal, {fmtMax(a.cardioMax)}.</p>
            <p>Total distance {a.cardioDistance.toFixed(2)} km.</p>
          </>
        }
      />
      <Section
        title="Weights"
        labels={a.labels}
        data={a.weightsKg}
        yLabel="Kg (× reps)"
        secondary={{ data: a.weightsCalories, yLabel: 'Calories' }}
        summary={
          <>
            <p>Averaged {avgWeights} kg (× reps) per weights day across {a.weightsDays} days. {frequencyMsg(a.weightsDays, windowDays, 'weights')}</p>
            <p>Averaged {avgWeightsCal} estimated calories per weights day.</p>
            <p>Record: <strong className="text-[var(--bac-safe)]">{Math.round(a.weightsMax.value)} kg</strong>, {fmtMax(a.weightsMax)}.</p>
          </>
        }
      />
      <Section
        title="Exercise"
        labels={a.labels}
        data={a.exerciseCalories}
        yLabel="Calories"
        summary={
          <>
            <p>Averaged {avgExercise} calories across {a.exerciseDays} exercise days. {frequencyMsg(a.exerciseDays, windowDays, 'exercise')}</p>
            <p>Record: <strong className="text-[var(--bac-safe)]">{Math.round(a.exerciseMax.value)}</strong> cal, {fmtMax(a.exerciseMax)}.</p>
            <p>Total distance {a.exerciseDistance.toFixed(2)} km.</p>
          </>
        }
      />
    </div>
  )
}
